import { NAVIGATION_ITEMS } from '@/shared/constants';
import { ThemeContext } from '@/shared/context';
import { AuthContext } from '@/shared/context/AuthContext';
import { classNames, RELEASE_NOTES_ROUTE, useResize } from '@/shared/utils';
import { LogoutOutlined, MenuOutlined, SunOutlined } from '@ant-design/icons';
import { Badge, Dropdown, Typography } from 'antd';
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styles from './index.module.css';
import DrawerProfile from '../DrawerProfile/DrawerProfile';
import { COMMON_LINKS, IGNORED_LINKS } from './NavBar.constants';
import logo from '@/shared/images/storm_icon-icons.com_67503.png';
import { projectConfig } from '@/shared/configs/projectConfig';

const NavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { isAdmin, userState, status } = useSelector(state => state.userInfo);
  const showUnreadIndicator = useSelector(state => state.ui.showUnreadIndicator);
  const translationGroup = useSelector(state => state.userInfo.translationGroup);
  const { toggleTheme } = useContext(ThemeContext);
  const { isMobile } = useResize();
  const { Title, Text } = Typography;

  const logoutHandler = event => {
    event.preventDefault();
    auth.logout();
    window.location.reload();
  };

  const BADGE_OPTIONS = useMemo(
    () => ({
      [NAVIGATION_ITEMS.GROUP_TRANSLATIONS.key]: typeof translationGroup === 'string',
      [NAVIGATION_ITEMS.TRANSLATION.key]: showUnreadIndicator,
    }),
    [translationGroup, showUnreadIndicator]
  );

  const items = Object.keys(NAVIGATION_ITEMS)
    .map(route => {
      const currentRoute = NAVIGATION_ITEMS[route];

      return {
        key: currentRoute.key,
        label: (
          <NavLink to={currentRoute.link} className={styles.NavHover}>
            <div
              className={classNames(styles.NavHover, {
                [styles.activeNavItem]: location.pathname === currentRoute.link,
              })}
            >
              <span className={styles.NavContainer}>
                {BADGE_OPTIONS[currentRoute.key] && (
                  <Badge dot={BADGE_OPTIONS[currentRoute.key]} className={styles.badge} />
                )}
                {currentRoute.label}
                {currentRoute.icon}
              </span>
            </div>
          </NavLink>
        ),
      };
    })
    .filter(el => {
      if (isAdmin || COMMON_LINKS.includes(el.key)) {
        return true;
      }

      if (!userState.responseAccesses) {
        return false;
      }

      return userState.responseAccesses[el.key];
    });

  if (isMobile) {
    items.push(
      {
        key: 'changeTheme',
        icon: <SunOutlined style={{ fontSize: '22px', color: 'var(--text-drawer)', paddingRight: '7px' }} />,
        label: (
          <button onClick={toggleTheme} className={styles.toggleThemeButton}>
            Сменить тему
          </button>
        ),
      },
      status === 'resolved' && {
        key: '1_exit',
        icon: <LogoutOutlined style={{ fontSize: '22px', color: 'var(--text-drawer)', paddingRight: '7px' }} />,
        label: (
          <a href='/' onClick={logoutHandler} className={styles.exitButton}>
            Выйти
          </a>
        ),
      }
    );
  }

  const SIDEBAR_LINKS = Object.values(NAVIGATION_ITEMS)
    .filter(el => {
      if (isAdmin || COMMON_LINKS.includes(el.key)) {
        return true;
      }

      if (!userState.responseAccesses) {
        return false;
      }

      return userState.responseAccesses[el.key];
    })
    .filter(link => !IGNORED_LINKS.includes(link.key));

  return isMobile ? (
    <div className={styles.mobileHeader}>
      <div className={styles.logoWrapper} onClick={() => isAdmin && navigate(RELEASE_NOTES_ROUTE)}>
        <img src={logo} alt='logo' className={styles.stormIcon} />
        <div className={styles.informationBox}>
          <Title level={4} style={{ marginBottom: 0 }}>
            STORM
          </Title>
          <Text type='secondary'>version {projectConfig.CURRENT_VERSION}</Text>
        </div>
      </div>
      <Dropdown menu={{ items }} trigger={['click']} placement='bottomLeft'>
        <button onClick={e => e.preventDefault()} className={styles.menuButton}>
          <MenuOutlined />
        </button>
      </Dropdown>
    </div>
  ) : (
    <>{status === 'resolved' && <DrawerProfile menuItems={SIDEBAR_LINKS} />}</>
  );
};

export default NavBar;
